import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import type { GuidePricing } from '@/types';
import { formatGuidePrice } from '@/features/guide/utils/pricing';

interface GuidePricingTableProps {
  pricing?: GuidePricing | null;
}

export function GuidePricingTable({ pricing }: GuidePricingTableProps) {
  if (!pricing) {
    return (
      <Typography color="text.secondary" variant="body2">
        Pricing not configured yet.
      </Typography>
    );
  }

  const pricingOptions = [
    {
      label: 'Few hours',
      value: pricing.hourlyPrice,
      icon: AccessTimeIcon,
      color: 'primary.main',
      bgColor: 'rgba(59, 130, 246, 0.08)',
    },
    {
      label: 'Full day',
      value: pricing.fullDayPrice,
      icon: WbSunnyIcon,
      color: 'secondary.main',
      bgColor: 'rgba(245, 158, 11, 0.08)',
    },
  ].filter((option) => option.value != null && option.value > 0);

  if (pricingOptions.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2">
        Pricing not configured yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {pricingOptions.map((option) => {
        const Icon = option.icon;
        return (
          <Paper
            key={option.label}
            elevation={0}
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' },
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: option.bgColor,
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 'var(--shadow-md)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '6px',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon sx={{ color: option.color, fontSize: 22 }} />
              </Box>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {option.label}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={800} color={option.color}>
              {formatGuidePrice(option.value, pricing.currency)}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
}
